/**
 * src/services/api.js
 * ───────────────────
 * Centralised API service layer.  All HTTP calls go through here — no page
 * component should import axios directly.  This means:
 *   • One place to update if a microservice URL prefix changes
 *   • Consistent auth header injection
 *   • Easy to mock in tests
 */
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const API  = `${BASE}/api`;

// ── Axios instance with auth interceptor ──────────────────────────────────────

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Auth service calls ────────────────────────────────────────────────────────

export const authService = {
  register: (payload)           => client.post('/auth/register', payload),
  registerOfficer: (payload)    => client.post('/auth/register/officer', payload),
  login:    (payload)           => client.post('/auth/login', payload),
  me:       ()                  => client.get('/auth/me'),
  googleSession: (sessionId)    =>
    client.post('/auth/google/session', {}, { headers: { 'X-Session-ID': sessionId } }),
  forgotPassword: (email)       => client.post('/auth/forgot-password', { email }),
  verifyOtp: (payload)          => client.post('/auth/verify-otp', payload),
  resetPassword: (payload)      => client.post('/auth/reset-password', payload),
};

// ── Violation service calls ───────────────────────────────────────────────────

export const violationService = {
  getTypes: ()         => client.get('/violation-types'),
  list:     (status)   => client.get('/violations', { params: status ? { status } : {} }),
  get:      (id)       => client.get(`/violations/${id}`),
  /** @param {FormData} formData */
  create:   (formData) =>
    client.post('/violations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Appeal a rejected violation
  appeal:   (id, reason) => client.post(`/violations/${id}/appeal`, { reason }),
};

// ── Officer service calls ─────────────────────────────────────────────────────

export const officerService = {
  list:   (params) => client.get('/officer/violations', { params }),
  review: (violationId, payload) =>
    client.post(`/officer/violations/${violationId}/review`, payload),
  // Review lock (concurrency): acquire/refresh + release
  lock:   (violationId) => client.post(`/officer/violations/${violationId}/lock`),
  unlock: (violationId) => client.delete(`/officer/violations/${violationId}/lock`),
  // Release that still fires during page unload/refresh — `keepalive` lets the
  // request outlive the document, where a normal axios call would be cancelled.
  unlockKeepalive: (violationId) => {
    const token = localStorage.getItem('token');
    try {
      return fetch(`${API}/officer/violations/${violationId}/lock`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        keepalive: true,
      });
    } catch {
      return Promise.resolve();
    }
  },
  // Approved-violation history for a vehicle plate
  vehicleHistory: (params) => client.get('/officer/vehicle-history', { params }),
  // Reporter credibility / track record
  reporterReputation: (userId) => client.get('/officer/reporter-reputation', { params: { user_id: userId } }),
};

// ── Admin service calls ───────────────────────────────────────────────────────

export const adminService = {
  stats:       ()         => client.get('/admin/stats'),
  violations:  (params)   => client.get('/admin/violations', { params }),
  users:       (role)     => client.get('/admin/users', { params: role ? { role } : {} }),
  updateRole:  (userId, role)         => client.patch(`/admin/users/${userId}/role`, null, { params: { role } }),
  suspendUser: (userId, suspended)    => client.patch(`/admin/users/${userId}/suspend`, null, { params: { suspended } }),
  apiQueue:    ()                     => client.get('/admin/api-queue'),
  retryApi:    (violationId)          => client.post(`/admin/api-queue/${violationId}/retry`),
  apiLogs:     (limit = 100)          => client.get('/admin/api-logs', { params: { limit } }),
  analytics:   ()                     => client.get('/admin/analytics'),
  officerApplications: ()             => client.get('/admin/officer-applications'),
  approveOfficer: (userId)            => client.patch(`/admin/officer-applications/${userId}/approve`),
  rejectOfficer:  (userId, reason)    => client.patch(`/admin/officer-applications/${userId}/reject`, null, { params: reason ? { reason } : {} }),
  auditLogs:          (limit = 100)   => client.get('/admin/audit-logs', { params: { limit } }),
  officerPerformance: ()              => client.get('/admin/officer-performance'),
};

// ── Notification service calls ────────────────────────────────────────────────

export const notificationService = {
  list:        ()      => client.get('/notifications'),
  unreadCount: ()      => client.get('/notifications/unread-count'),
  markRead:    (id)    => client.post(`/notifications/${id}/read`),
  markAllRead: ()      => client.post('/notifications/read-all'),
};

// ── Media service helper ──────────────────────────────────────────────────────

export const mediaUrl = (path, token) =>
  `${API}/files/${path}${token ? `?auth=${token}` : ''}`;

export default client;
